<template>
  <!-- Show Invoice Details in a Popup Modal -->
  <div v-if="showSettleInvoicePaymentModal" ref="modalContent" class="modal-overlay">
    <div class="modal-content" @click.stop>
      <!-- Fixed Header -->
      <div class="modal-header-fixed">
        <div class="popup-header">
          <h3>Settle Invoice Due Payments</h3>
          <button @click="closeSettleInvoicePaymentModal" class="btn btn-light close-btn">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>

      <!-- Scrollable Body -->
      <div class="modal-body-scrollable">
        <!-- Header Content -->
        <div class="header-content">
          <div class="dealer-info-box">
            <div class="dealer-info-content">
              <!-- GROUP MODE OF PAYMENT -->
              <template v-if="isGroupMode">
                <div class="dealer-info-item">
                  <i class="fa-solid fa-user"></i>
                  <div class="dealer-info-text">
                    <div class="dealer-info-label">Dealer Group</div>
                    <div class="dealer-info-value">
                      {{ dealerGroup && dealerGroup.group_name }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- INDIVIDUAL MODE OF PAYMENT -->
              <template v-else>
                <div class="dealer-info-item">
                  <i class="fa-solid fa-user"></i>
                  <div class="dealer-info-text">
                    <div class="dealer-info-label">Business Name</div>
                    <div class="dealer-info-value">
                      {{ dealer && dealer.business_display_name }}
                    </div>
                  </div>
                </div>
                <div class="dealer-info-item">
                  <i class="fa-solid fa-location-dot"></i>
                  <div class="dealer-info-text">
                    <div class="dealer-info-label">Address</div>
                    <div class="dealer-info-value">{{ dealer && dealer.address }}</div>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <!-- Amount Cards -->
          <div class="amount-cards-container">
            <div class="amount-card due-card">
              <div class="amount-card-content">
                <div class="amount-card-label">Selected Due</div>
                <div class="amount-card-value">Rs. {{ formatNumber(totalDue) }}</div>
              </div>
            </div>
            <div class="amount-card total-card">
              <div class="amount-card-content">
                <div class="amount-card-label">Grand Total</div>
                <div class="amount-card-value">Rs. {{ formatNumber(getGrandTotal()) }}</div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="loadingSettlePopup" class="spinner-container">
          <div class="spinner"></div>
        </div>

        <template v-else>
          <!-- SHARED GLOBAL PAYMENT FORM -->
          <div class="global-payment-section mb-4" style="padding: 0 25px;">
            <div class="global-payment-card bg-white" style="border-radius: 10px; box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1); overflow: hidden; border: 1px solid #e0e0e0;">
              <div style="padding: 15px 20px; border-bottom: 1px solid #e0e0e0; background: #f8f9fa; display: flex; justify-content: space-between; align-items: center;">
                <h6 class="mb-0 font-weight-bold" style="color: #333; font-size: 15px;">Global Payment (to be allocated)</h6>
                <span v-if="globalAddedPayment" class="text-success" style="font-weight: 700; font-size: 1.1em;">
                  Remaining: Rs. {{ formatNumber(globalRemainingAmount) }}
                </span>
              </div>
              <div v-if="globalAddedPayment && globalAddedPayment.amount < totalSelectedDue" style="background-color: #fff3cd; color: #856404; padding: 10px 20px; border-bottom: 1px solid #ffeeba; font-size: 13px; display: flex; align-items: center;">
                <i class="fa-solid fa-circle-exclamation mr-2" style="font-size: 15px;"></i>
                <span><strong>&nbsp;Note:</strong> The added payment (Rs. {{ formatNumber(globalAddedPayment.amount) }}) is less than the total due amount of selected invoices (Rs. {{ formatNumber(totalSelectedDue) }}).</span>
              </div>
              <div v-else-if="globalAddedPayment && globalAddedPayment.amount > totalSelectedDue" style="background-color: #fff3cd; color: #856404; padding: 10px 20px; border-bottom: 1px solid #ffeeba; font-size: 13px; display: flex; align-items: center;">
                <i class="fa-solid fa-circle-exclamation mr-2" style="font-size: 15px;"></i>
                <span><strong>&nbsp;Note:</strong> The added payment (Rs. {{ formatNumber(globalAddedPayment.amount) }}) is greater than the total due amount of selected invoices (Rs. {{ formatNumber(totalSelectedDue) }}).</span>
              </div>
              <div class="card-body" style="padding: 20px;">
                <div class="global-payment-form">
                   <div class="row align-items-end">
                     <div class="col-md-3 form-group mb-3">
                       <label>Payment Method <span class="req">*</span></label>
                       <select v-model="globalPaymentForm.temp_payment_method" @change="onGlobalPaymentMethodChange" class="form-select" :disabled="globalAddedPayment !== null">
                         <option disabled value="">-- Select --</option>
                         <option value="Cash">Cash</option>
                         <option value="Cheque">Cheque</option>
                         <option value="Bank Transfer">Bank Transfer</option>
                         <option v-if="!isGroupMode" value="Advance">Advance Payment</option>
                         <option value="Credit/Debit Card">Credit/Debit Card</option>
                         <option value="Mobile Payment">Mobile Payment</option>
                         <option value="Online Payment">Online Payment</option>
                       </select>
                     </div>

                     <!-- Cheque Details -->
                     <template v-if="globalPaymentForm.temp_payment_method === 'Cheque'">
                       <div class="col-md-3 form-group mb-3">
                         <label>Issue Bank <span class="req">*</span></label>
                         <select v-model="globalPaymentForm.cheque_issue_bank_id" @change="loadGlobalBankBranchList" class="form-select" :disabled="globalAddedPayment !== null">
                           <option disabled value="">-- Select --</option>
                           <option v-for="bank in allbankList" :key="bank.id" :value="bank.id">{{ bank.bank_name }}</option>
                         </select>
                       </div>
                       <div class="col-md-3 form-group mb-3">
                         <label>Issue Branch <span class="req">*</span></label>
                         <select v-model="globalPaymentForm.cheque_issue_branch_id" class="form-select" :disabled="globalAddedPayment !== null">
                           <option disabled value="">-- Select --</option>
                           <option v-for="branch in globalPaymentForm.branchesList" :key="branch.id" :value="branch.id">{{ branch.branch_name }}</option>
                         </select>
                       </div>
                       <div class="col-md-3 form-group mb-3">
                         <label>Cheque No <span class="req">*</span></label>
                         <input type="text" v-model="globalPaymentForm.cheque_no" placeholder="Cheque No" class="form-control" :disabled="globalAddedPayment !== null" />
                       </div>
                       <div class="col-md-3 form-group mb-3">
                         <label>Issue Date <span class="req">*</span></label>
                         <input type="date" v-model="globalPaymentForm.cheque_date" class="form-control" :disabled="globalAddedPayment !== null" />
                       </div>
                     </template>

                     <!-- Bank Transfer Details -->
                     <template v-else-if="globalPaymentForm.temp_payment_method === 'Bank Transfer'">
                       <div class="col-md-4 form-group mb-3">
                         <label>Bank Account <span class="req">*</span></label>
                         <select v-model="globalPaymentForm.bank_account" class="form-select" :disabled="globalAddedPayment !== null">
                           <option disabled value="">-- Select --</option>
                           <option v-for="bank in bankList" :key="bank.id" :value="bank.id">
                             {{ bank.bank_name }} - {{ maskBankAccountNo(bank.account_no) }}
                           </option>
                         </select>
                       </div>
                       <div class="col-md-3 form-group mb-3">
                         <label>Reference Code <span class="req">*</span></label>
                         <input type="text" v-model="globalPaymentForm.trans_ref_code" placeholder="Ref Code" class="form-control" :disabled="globalAddedPayment !== null" />
                       </div>
                     </template>

                     <!-- Advance Payment Details -->
                     <template v-else-if="globalPaymentForm.temp_payment_method === 'Advance'">
                       <div class="col-md-6 form-group mb-3">
                         <label>Select Advance Payment <span class="req">*</span></label>
                         <select v-model="globalPaymentForm.selected_advance_id" class="form-select" :disabled="globalAddedPayment !== null">
                           <option disabled value="">-- Select Advance Payment --</option>
                           <option
                             v-for="adv in localAdvancePayments"
                             :key="adv.id"
                             :value="adv.id"
                             :disabled="getAdvanceRemaining(adv.id) <= 0"
                           >
                             {{ adv.pay_method }} - Rs.{{ formatNumber(getAdvanceRemaining(adv.id)) }} ({{ formatDateTime(adv.paid_at) }})
                           </option>
                         </select>
                       </div>
                     </template>

                     <!-- Credit/Debit Card Details -->
                     <template v-else-if="globalPaymentForm.temp_payment_method === 'Credit/Debit Card'">
                       <div class="col-md-3 form-group mb-3">
                         <label>Card Type <span class="req">*</span></label>
                         <select v-model="globalPaymentForm.card_type" class="form-select" :disabled="globalAddedPayment !== null">
                           <option disabled value="">-- Select --</option>
                           <option value="Visa">Visa</option>
                           <option value="MasterCard">MasterCard</option>
                           <option value="American Express">American Express</option>
                           <option value="Discover">Discover</option>
                           <option value="UnionPay">UnionPay</option>
                           <option value="JCB">JCB</option>
                           <option value="Diners Club">Diners Club</option>
                         </select>
                       </div>
                       <div class="col-md-3 form-group mb-3">
                         <label>Last 4 Digits <span class="req">*</span></label>
                         <input
                           type="text"
                           v-model="globalPaymentForm.card_last_four"
                           placeholder="e.g. 1234"
                           maxlength="4"
                           class="form-control"
                           @input="globalPaymentForm.card_last_four = globalPaymentForm.card_last_four.replace(/\D/g, '')"
                           :disabled="globalAddedPayment !== null"
                         />
                       </div>
                     </template>

                     <!-- Mobile Payment Details -->
                     <template v-else-if="globalPaymentForm.temp_payment_method === 'Mobile Payment'">
                       <div class="col-md-3 form-group mb-3">
                         <label>Type <span class="req">*</span></label>
                         <select v-model="globalPaymentForm.mobile_payment_type" class="form-select" :disabled="globalAddedPayment !== null">
                           <option disabled value="">-- Select --</option>
                           <option value="QR">QR</option>
                           <option value="Wallet">Wallet</option>
                           <option value="Bank Mobile Apps">Bank Mobile Apps</option>
                         </select>
                       </div>
                       <div class="col-md-3 form-group mb-3">
                         <label>Provider <span class="req">*</span></label>
                         <input type="text" v-model="globalPaymentForm.mobile_provider" placeholder="Provider" class="form-control" :disabled="globalAddedPayment !== null" />
                       </div>
                       <div class="col-md-3 form-group mb-3">
                         <label>Reference No <span class="req">*</span></label>
                         <input type="text" v-model="globalPaymentForm.trans_ref_code" placeholder="Ref No" class="form-control" :disabled="globalAddedPayment !== null" />
                       </div>
                     </template>

                     <!-- Online Payment Details -->
                     <template v-else-if="globalPaymentForm.temp_payment_method === 'Online Payment'">
                       <div class="col-md-3 form-group mb-3">
                         <label>Payment Gateway <span class="req">*</span></label>
                         <input type="text" v-model="globalPaymentForm.online_gateway" placeholder="Gateway" class="form-control" :disabled="globalAddedPayment !== null" />
                       </div>
                       <div class="col-md-3 form-group mb-3">
                         <label>Reference No <span class="req">*</span></label>
                         <input type="text" v-model="globalPaymentForm.trans_ref_code" placeholder="Ref No" class="form-control" :disabled="globalAddedPayment !== null" />
                       </div>
                     </template>

                     <!-- Payment Amount -->
                     <div class="col-md-3 form-group mb-3">
                       <label>Amount (Rs) <span class="req">*</span></label>
                       <input
                         v-model="globalPaymentForm.temp_payment_amount"
                         type="number"
                         placeholder="0.00"
                         class="form-control"
                         :disabled="globalAddedPayment !== null"
                       />
                     </div>

                     <div class="col-md-3 form-group mb-3">
                       <button v-if="globalAddedPayment" type="button" class="btn btn-danger w-100" @click="removeGlobalPayment" style="border-radius: 50px; font-size: 12px; padding: 8px 16px;">
                         <i class="fa-solid fa-trash"></i> Remove Payment
                       </button>
                       <button v-else type="button" class="btn btn-add w-100" @click="addGlobalPayment">
                         <i class="fa-solid fa-plus"></i> Add Payment
                       </button>
                     </div>
                   </div>
                </div>
              </div>
            </div>
          </div>

          <!-- INVOICE TABLES -->
            <template v-if="isGroupMode">
              <div
                v-for="dealerSection in groupedByDealer"
                :key="dealerSection.dealerId"
                class="dealer-section mb-4"
                style="padding: 0 25px;"
              >
                <div class="dealer-card bg-white" style="border-radius: 10px; box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1); overflow: hidden; border: 1px solid #e0e0e0;">
                  <!-- Dealer Header -->
                  <div class="dealer-section-header" style="background-color: #ffffff; border-bottom: 1px solid #e0e0e0; padding: 15px 20px; display: flex; justify-content: space-between; align-items: center;">
                    <div>
                      <strong style="color: #333; font-size: 15px;">{{ dealerSection.dealerName }}</strong>
                      <span class="text-muted ms-2" style="font-size: 0.85rem">
                        {{ dealerSection.invoices.length }} invoice(s)
                      </span>
                    </div>
                    <div style="display: flex; gap: 20px; font-size: 14px; align-items: center;">
                      <span><strong>Total Due:</strong> Rs. {{ formatNumber(getDealerTotalDue(dealerSection.dealerId)) }}</span>
                      <span class="text-primary"><strong>Allocated:</strong> Rs. {{ formatNumber(getDealerTotalAllocated(dealerSection.dealerId)) }}</span>
                      <button class="btn btn-sm btn-outline-secondary" @click="toggleDealer(dealerSection.dealerId)" style="padding: 2px 8px; border-radius: 4px;" title="Toggle Invoices">
                        <i :class="collapsedDealers[dealerSection.dealerId] ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-up'"></i>
                      </button>
                    </div>
                  </div>
                  
                  <!-- Dealer invoices table -->
                  <div v-show="!collapsedDealers[dealerSection.dealerId]" class="table-responsive invoice-table-container">
                    <table class="table invoice-table table-hover bg-white mb-0" style="border: none;">
                      <thead style="background-color: #f8f9fa;">
                        <tr>
                          <th style="font-weight: 600; color: #495057; border-bottom: 2px solid #dee2e6;">Invoice No</th>
                          <th style="font-weight: 600; color: #495057; border-bottom: 2px solid #dee2e6;">Date</th>
                          <th class="text-right" style="font-weight: 600; color: #495057; border-bottom: 2px solid #dee2e6;">Net Total</th>
                          <th class="text-right" style="font-weight: 600; color: #495057; border-bottom: 2px solid #dee2e6;">Paid</th>
                          <th class="text-right" style="font-weight: 600; color: #495057; border-bottom: 2px solid #dee2e6;">Due</th>
                          <th style="width: 180px; font-weight: 600; color: #495057; border-bottom: 2px solid #dee2e6;">Allocate Amount</th>
                          <th style="width: 90px; font-weight: 600; color: #495057; border-bottom: 2px solid #dee2e6; text-align: center;">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="invoice in dealerSection.invoices" :key="invoice.invoices_no">
                          <td style="color: #333; vertical-align: middle; font-weight: 500;">{{ invoice.invoices_no }}</td>
                          <td style="color: #555; vertical-align: middle;">{{ formatDateTime(invoice.invoice_created_at) }}</td>
                          <td class="text-right" style="color: #333; vertical-align: middle;">Rs. {{ formatNumber(invoice.net_total) }}</td>
                          <td class="text-right text-success" style="vertical-align: middle; font-weight: 500;">Rs. {{ formatNumber(getInvoiceTotalPaid(invoice.invoices_no)) }}</td>
                          <td class="text-right text-danger" style="vertical-align: middle; font-weight: 600;">Rs. {{ formatNumber(getCurrentDueAmount(invoice)) }}</td>
                          <td style="vertical-align: middle;">
                            <div v-if="getInvoicePayments(invoice.invoices_no).length > 0">
                               <strong class="text-primary" style="font-size: 1.05em;">Rs. {{ formatNumber(getInvoicePayments(invoice.invoices_no)[0].amount) }}</strong>
                            </div>
                            <div v-else>
                               <input 
                                 type="number" 
                                 class="form-control form-control-sm" 
                                 v-model="allocatedAmounts[invoice.invoices_no]"
                                 :disabled="!globalAddedPayment || globalRemainingAmount <= 0"
                                 style="border-radius: 4px; border: 1px solid #ced4da;"
                               />
                            </div>
                          </td>
                          <td style="vertical-align: middle; text-align: center;">
                            <button v-if="getInvoicePayments(invoice.invoices_no).length > 0" 
                                    type="button"
                                    class="btn btn-sm btn-outline-danger"
                                    style="border-radius: 4px; padding: 4px 10px;"
                                    @click="removeAllocation(invoice.invoices_no)"
                                    title="Remove Allocation">
                              <i class="fa-solid fa-xmark"></i>
                            </button>
                            <button v-else 
                                    type="button"
                                    class="btn btn-sm btn-success" 
                                    style="border-radius: 4px; padding: 4px 12px; font-weight: 500;"
                                    :disabled="!globalAddedPayment || !allocatedAmounts[invoice.invoices_no]"
                                    @click="addAllocation(invoice)"
                                    title="Add Allocation">
                              <i class="fa-solid fa-check"></i> Add
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </template>
            
            <template v-else>
              <div class="table-responsive invoice-table-container mb-4" style="padding: 0 25px;">
                <table class="table invoice-table table-hover bg-white" style="border: 1px solid #e0e0e0; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); overflow: hidden;">
                  <thead style="background-color: #f8f9fa;">
                    <tr>
                      <th style="font-weight: 600; color: #495057; border-bottom: 2px solid #dee2e6;">Invoice No</th>
                      <th style="font-weight: 600; color: #495057; border-bottom: 2px solid #dee2e6;">Date</th>
                      <th class="text-right" style="font-weight: 600; color: #495057; border-bottom: 2px solid #dee2e6;">Net Total</th>
                      <th class="text-right" style="font-weight: 600; color: #495057; border-bottom: 2px solid #dee2e6;">Paid</th>
                      <th class="text-right" style="font-weight: 600; color: #495057; border-bottom: 2px solid #dee2e6;">Due</th>
                      <th style="width: 180px; font-weight: 600; color: #495057; border-bottom: 2px solid #dee2e6;">Allocate Amount</th>
                      <th style="width: 90px; font-weight: 600; color: #495057; border-bottom: 2px solid #dee2e6; text-align: center;">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="invoice in selectedInvoices" :key="invoice.invoices_no">
                      <td style="color: #333; vertical-align: middle; font-weight: 500;">{{ invoice.invoices_no }}</td>
                      <td style="color: #555; vertical-align: middle;">{{ formatDateTime(invoice.invoice_created_at) }}</td>
                      <td class="text-right" style="color: #333; vertical-align: middle;">Rs. {{ formatNumber(invoice.net_total) }}</td>
                      <td class="text-right text-success" style="vertical-align: middle; font-weight: 500;">Rs. {{ formatNumber(getInvoiceTotalPaid(invoice.invoices_no)) }}</td>
                      <td class="text-right text-danger" style="vertical-align: middle; font-weight: 600;">Rs. {{ formatNumber(getCurrentDueAmount(invoice)) }}</td>
                      <td style="vertical-align: middle;">
                        <div v-if="getInvoicePayments(invoice.invoices_no).length > 0">
                           <strong class="text-primary" style="font-size: 1.05em;">Rs. {{ formatNumber(getInvoicePayments(invoice.invoices_no)[0].amount) }}</strong>
                        </div>
                        <div v-else>
                           <input 
                             type="number" 
                             class="form-control form-control-sm" 
                             v-model="allocatedAmounts[invoice.invoices_no]"
                             :disabled="!globalAddedPayment || globalRemainingAmount <= 0"
                             style="border-radius: 4px; border: 1px solid #ced4da;"
                           />
                        </div>
                      </td>
                      <td style="vertical-align: middle; text-align: center;">
                        <button v-if="getInvoicePayments(invoice.invoices_no).length > 0" 
                                type="button"
                                class="btn btn-sm btn-outline-danger"
                                style="border-radius: 4px; padding: 4px 10px;"
                                @click="removeAllocation(invoice.invoices_no)"
                                title="Remove Allocation">
                          <i class="fa-solid fa-xmark"></i>
                        </button>
                        <button v-else 
                                type="button"
                                class="btn btn-sm btn-success" 
                                style="border-radius: 4px; padding: 4px 12px; font-weight: 500;"
                                :disabled="!globalAddedPayment || !allocatedAmounts[invoice.invoices_no]"
                                @click="addAllocation(invoice)"
                                title="Add Allocation">
                          <i class="fa-solid fa-check"></i> Add
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
            
            <!-- Save Button -->
            <div class="d-flex justify-content-end save-button-container" style="padding: 0 25px; margin-bottom: 20px;">
              <button
                @click="saveInvoicePayment"
                :disabled="saveButtonDisabled"
                class="btn btn-success save-btn"
              >
                <span v-if="saveButtonDisabled" class="spinner-border spinner-border-sm"></span>
                <span v-else>Save All Payments</span>
              </button>
            </div>
        </template>

    </div>
  </div>
</div>

  <!-- Custom Confirmation Dialog Box for Delete Payments -->
  <div v-if="showConfirmationModal" class="modal-overlay confirmation-overlay">
    <div class="confirmation-modal">
      <div class="confirmation-header">
        <h4>{{ confirmationTitle }}</h4>
      </div>
      <div class="confirmation-body">
        <p>{{ confirmationMessage }}</p>
      </div>
      <div class="confirmation-footer">
        <button class="btn btn-danger" @click="cancelConfirmation">No</button>
        <button class="btn btn-success" @click="confirmAction">Yes</button>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { saveCompletedPay, getBanksByLoginUser, getBankList, getBranchesByBankId, getAdvancePaymentsByDealer } from "@/services/dataService";

export default {
  props: {
    showSettleInvoicePaymentModal: Boolean,
    dealer: Object,
    totalExcessAmount: Number,
    totalDue: Number,
    selectedInvoices: Array,
    loadingSettlePopup: Boolean,
    isGroupMode: { type: Boolean, default: false },
    dealerGroup: { type: Object, default: null },
  },
  emits: ['close'],

  data() {
    return {
      token: localStorage.getItem('token_set') || '',
      profileId: localStorage.getItem('profile_id'),
      saveButtonDisabled: false,
      invoicePayments: [],
      bankList: [],
      allbankList: [],
      localAdvancePaymentsByDealer: {},
      expandedInvoices: {},
      scrollStates: {},
      paymentListRefs: {},
      paymentForms: {},
      showConfirmationModal: false,
      confirmationTitle: '',
      confirmationMessage: '',
      pendingAction: null,
      pendingActionParams: null,
      globalPaymentForm: {
        temp_payment_method: '',
        temp_payment_amount: '',
        selected_advance_id: '',
        cheque_issue_bank_id: '',
        cheque_issue_branch_id: '',
        cheque_no: '',
        cheque_date: '',
        bank_account: '',
        trans_ref_code: '',
        card_type: '',
        card_last_four: '',
        mobile_payment_type: '',
        mobile_provider: '',
        online_gateway: '',
        branchesList: [],
      },
      globalAddedPayment: null,
      allocatedAmounts: {},
      collapsedDealers: {}
    };
  },

  mounted() {
    this.loadBankList();
    this.loadAllBankList();
  },

  watch: {
    selectedInvoices: {
      immediate: true,
      handler(newVal) {
        if (!newVal) return;
        newVal.forEach(inv => {
          if (!this.paymentForms[inv.invoices_no]) {
            this.paymentForms[inv.invoices_no] = {
              temp_payment_method: '',
              temp_payment_amount: '',
              selected_advance_id: '',
              cheque_issue_bank_id: '',
              cheque_issue_branch_id: '',
              cheque_no: '',
              cheque_date: '',
              bank_account: '',
              trans_ref_code: '',
              card_type: '',
              card_last_four: '',
              mobile_payment_type: '',
              mobile_provider: '',
              online_gateway: '',
              branchesList: [],
            };
          }
        });
      }
    },
    showSettleInvoicePaymentModal: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.fetchAdvancePayments();
        }
      }
    }
  },

  computed: {
    localAdvancePayments() {
      const pId = this.dealer ? this.dealer.profile_id : null;
      return pId ? (this.localAdvancePaymentsByDealer[pId] || []) : [];
    },
    // FIX: Group strictly by profile_id (the unique dealer identifier).
    // The old code also used profile_id as the key but relied on invoice.business_name
    // for the display label — that part is fine. The real problem was that the parent
    // was sometimes sending invoices without a profile_id field populated, so every
    // invoice fell into the same 'undefined' bucket.
    // Now we also expose a helper `getAdvancesForDealer` that the template calls with
    // invoice.profile_id so each invoice gets only its dealer's advances.
    globalRemainingAmount() {
      if (!this.globalAddedPayment) return 0;
      const allocatedTotal = this.invoicePayments.reduce((sum, p) => sum + Number(p.amount), 0);
      return Math.max(0, this.globalAddedPayment.amount - allocatedTotal);
    },
    totalSelectedDue() {
      if (!this.selectedInvoices || this.selectedInvoices.length === 0) return 0;
      return this.selectedInvoices.reduce((sum, invoice) => {
        return sum + this.getCurrentDueAmount(invoice);
      }, 0);
    },
    groupedByDealer() {
      if (!this.isGroupMode) return [];

      const map = this.selectedInvoices.reduce((acc, invoice) => {
        // profile_id is normalised in the parent's performSearch(); use it as the key.
        const dealerId = invoice.profile_id || '';
        if (!acc[dealerId]) {
          acc[dealerId] = {
            dealerId,
            dealerName: invoice.business_name || invoice.business_display_name || 'Unknown Dealer',
            invoices: []
          };
        }
        acc[dealerId].invoices.push(invoice);
        return acc;
      }, {});

      return Object.values(map);
    }
  },

  methods: {
    getDealerTotalDue(dealerId) {
      const section = this.groupedByDealer.find(s => s.dealerId === dealerId);
      if (!section) return 0;
      return section.invoices.reduce((sum, inv) => sum + this.getCurrentDueAmount(inv), 0);
    },
    getDealerTotalAllocated(dealerId) {
      const section = this.groupedByDealer.find(s => s.dealerId === dealerId);
      if (!section) return 0;
      return section.invoices.reduce((sum, inv) => {
        let amount = 0;
        const payments = this.getInvoicePayments(inv.invoices_no);
        if (payments && payments.length > 0) {
          amount = payments[0].amount;
        } else if (this.allocatedAmounts[inv.invoices_no]) {
          amount = parseFloat(this.allocatedAmounts[inv.invoices_no]) || 0;
        }
        return sum + amount;
      }, 0);
    },
    toggleDealer(dealerId) {
      this.collapsedDealers[dealerId] = !this.collapsedDealers[dealerId];
    },
    onGlobalPaymentMethodChange() {
      const form = this.globalPaymentForm;
      form.selected_advance_id = '';
      form.cheque_issue_bank_id = '';
      form.cheque_issue_branch_id = '';
      form.cheque_no = '';
      form.cheque_date = '';
      form.bank_account = '';
      form.trans_ref_code = '';
      form.card_type = '';
      form.card_last_four = '';
      form.mobile_payment_type = '';
      form.mobile_provider = '';
      form.online_gateway = '';
      form.branchesList = [];
    },
    async loadGlobalBankBranchList() {
      try {
        if (!this.globalPaymentForm.cheque_issue_bank_id) return;
        const response = await getBranchesByBankId({ bank_id: this.globalPaymentForm.cheque_issue_bank_id }, this.token);
        this.globalPaymentForm.branchesList = response.data.data || [];
      } catch (error) {
        console.error('Error fetching Branches:', error);
      }
    },
    addGlobalPayment() {
      const toast = useToast();
      const form = this.globalPaymentForm;

      const amount = Number(form.temp_payment_amount);
      if (!form.temp_payment_method || !amount || amount <= 0) {
        toast.error("Please select payment method and enter a valid amount greater than 0.");
        return;
      }

      if (form.temp_payment_method === 'Advance') {
        const remaining = this.getAdvanceRemaining(form.selected_advance_id, null);
        if (!form.selected_advance_id) { toast.error("Please select an advance payment."); return; }
        if (Number(form.temp_payment_amount) > remaining) { toast.error("Not enough balance in advance!"); return; }
      }

      if (form.temp_payment_method === 'Credit/Debit Card') {
        if (!form.card_type) { toast.error('Please select card type.'); return; }
        if (!form.card_last_four || !/^\d{4}$/.test(form.card_last_four)) { toast.error('Valid 4 digits required.'); return; }
      }
      if (form.temp_payment_method === 'Mobile Payment') {
        if (!form.mobile_payment_type || !form.mobile_provider || !form.trans_ref_code) { toast.error('Fill mobile payment fields.'); return; }
      }
      if (form.temp_payment_method === 'Online Payment') {
        if (!form.online_gateway || !form.trans_ref_code) { toast.error('Fill online payment fields.'); return; }
      }

      this.globalAddedPayment = {
        payment_method: form.temp_payment_method,
        amount: amount,
        advance_ref_id: form.selected_advance_id || null,
        advance_ref: form.selected_advance_id ? "Advance Ref" : null,
        cheque_issue_bank_id: form.cheque_issue_bank_id || null,
        cheque_issue_branch_id: form.cheque_issue_branch_id || null,
        cheque_no: form.cheque_no || null,
        cheque_date: form.cheque_date || null,
        bank_account: form.bank_account || null,
        trans_ref_code: form.trans_ref_code || null,
        card_type: form.card_type || null,
        card_last4_digits: form.card_last_four || null,
        mobile_payment_type: form.mobile_payment_type || null,
        mobile_provider: form.mobile_provider || null,
        online_gateway: form.online_gateway || null,
      };

      toast.success("Global payment added successfully!");
    },
    removeGlobalPayment() {
      if (this.invoicePayments.length > 0) {
        useToast().error("Please clear all allocated invoice payments before removing the global payment.");
        return;
      }
      this.globalAddedPayment = null;
    },
    addAllocation(invoice) {
      const amount = Number(this.allocatedAmounts[invoice.invoices_no]);
      const toast = useToast();
      
      if (!amount || amount <= 0) {
        toast.error("Please enter a valid amount.");
        return;
      }
      
      const due = this.getCurrentDueAmount(invoice);
      if (amount > due) {
        toast.error("Allocated amount cannot exceed the due amount for this invoice.");
        return;
      }
      
      if (amount > this.globalRemainingAmount) {
        toast.error("Allocated amount cannot exceed the remaining global payment.");
        return;
      }

      const paymentIndex = this.invoicePayments.length;
      const paymentData = {
        ...this.globalAddedPayment,
        invoice_no: invoice.invoices_no,
        date: new Date().toISOString(),
        amount: amount,
        index: paymentIndex
      };
      
      this.invoicePayments.push(paymentData);
      this.allocatedAmounts[invoice.invoices_no] = ''; 
      toast.success("Allocated to invoice successfully.");
    },
    removeAllocation(invoiceNo) {
      this.invoicePayments = this.invoicePayments.filter(p => p.invoice_no !== invoiceNo);
      this.invoicePayments.forEach((payment, i) => { payment.index = i; });
    },
    // Returns (and initializes if needed) the payment form state for an invoice
    getPaymentForm(invoiceNo) {
      if (!this.paymentForms[invoiceNo]) {
        this.paymentForms[invoiceNo] = {
          temp_payment_method: '',
          temp_payment_amount: '',
          selected_advance_id: '',
          cheque_issue_bank_id: '',
          cheque_issue_branch_id: '',
          cheque_no: '',
          cheque_date: '',
          bank_account: '',
          trans_ref_code: '',
          card_type: '',
          card_last_four: '',
          mobile_payment_type: '',
          mobile_provider: '',
          online_gateway: '',
          branchesList: [],
        };
      }
      return this.paymentForms[invoiceNo];
    },

    formatNumber(value) {
      if (!value && value !== 0) return '0.00';
      return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    formatDateTime(datetime) {
      if (!datetime) return '';
      const date = new Date(datetime);
      if (Number.isNaN(date.getTime())) return String(datetime);
      return date.toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true,
      });
    },

    maskBankAccountNo(accountNo) {
      if (!accountNo) return '';
      const account = accountNo.toString();
      if (account.length <= 4) return account;
      return '*'.repeat(account.length - 4) + account.slice(-4);
    },

    async fetchAdvancePayments() {
      if (this.isGroupMode) return;

      const dealerIds = new Set();
      if (this.dealer && this.dealer.profile_id) {
        dealerIds.add(this.dealer.profile_id);
      }
      
      const newAdvancesByDealer = {};
      
      for (let dealerId of dealerIds) {
        try {
          const response = await getAdvancePaymentsByDealer(dealerId, this.token);
          newAdvancesByDealer[dealerId] = response.data?.advance_payments || response.data || [];
        } catch (error) {
          console.error(`Failed to fetch advance payments for dealer ${dealerId}`, error);
          newAdvancesByDealer[dealerId] = [];
        }
      }
      this.localAdvancePaymentsByDealer = newAdvancesByDealer;
    },

    // FIX: New helper — returns the advance payments list for a specific dealer.
    // In group mode we use localAdvancePaymentsByDealer keyed by profile_id.
    getAdvancesForDealer(dealerId) {
      if (!dealerId) return [];
      return this.localAdvancePaymentsByDealer[dealerId] || [];
    },

    // FIX: getAdvanceRemaining now accepts an optional dealerId so group-mode
    // invoices look up advances from the correct dealer's pool.
    getAdvanceRemaining(advanceId, dealerId = null) {
      let pool;
      if (this.isGroupMode && dealerId) {
        pool = this.getAdvancesForDealer(dealerId);
      } else {
        pool = this.localAdvancePayments;
      }

      const adv = pool.find(a => a.id === advanceId);
      if (!adv) return 0;

      const used = this.invoicePayments
          .filter(p => p.advance_ref_id === advanceId)
          .reduce((sum, p) => sum + Number(p.amount), 0);

      return (parseFloat(adv.balance) || 0) - used;
    },

    getGrandTotal() {
      return this.invoicePayments.reduce((total, payment) => total + Number(payment.amount), 0);
    },

    getCurrentDueAmount(invoice) {
      const originalDue = Number(invoice.original_due_amount) || 0;
      const paidAmount = this.getInvoicePayments(invoice.invoices_no)
          .reduce((total, payment) => total + Number(payment.amount), 0);
      return Math.max(0, originalDue - paidAmount);
    },

    fillCurrentDueAmount(invoice) {
      const form = this.getPaymentForm(invoice.invoices_no);
      form.temp_payment_amount = this.getCurrentDueAmount(invoice);
      useToast().info(`Filled current due amount: Rs. ${this.formatNumber(form.temp_payment_amount)}`);
    },

    validateAmount(invoice) {
      const form = this.getPaymentForm(invoice.invoices_no);
      const currentDue = this.getCurrentDueAmount(invoice);
      if (Number(form.temp_payment_amount) > currentDue) {
        form.temp_payment_amount = currentDue;
        useToast().error("Entered amount exceeds the current due amount!");
      }
    },

    async loadBankList() {
      try {
        const response = await getBanksByLoginUser(this.profileId, this.token);
        this.bankList = response.data.data || [];
      } catch (error) {
        console.error('Error fetching Banks:', error);
      }
    },

    async loadAllBankList() {
      try {
        const response = await getBankList(this.token);
        this.allbankList = response.data.data || [];
      } catch (error) {
        console.error('Error fetching all banks:', error);
      }
    },

    async loadBankBranchList(invoice) {
      try {
        const form = this.getPaymentForm(invoice.invoices_no);
        if (!form.cheque_issue_bank_id) return;
        const response = await getBranchesByBankId({ bank_id: form.cheque_issue_bank_id }, this.token);
        form.branchesList = response.data.data || [];
      } catch (error) {
        console.error('Error fetching Branches:', error);
      }
    },

    addInvoicePayment(invoice) {
      const toast = useToast();
      const form = this.getPaymentForm(invoice.invoices_no);

      if (!form.temp_payment_method || !form.temp_payment_amount) {
        toast.error("Please select payment method and enter amount.");
        return;
      }

      if (form.temp_payment_method === 'Advance') {
        const remaining = this.getAdvanceRemaining(
            form.selected_advance_id,
            this.isGroupMode ? invoice.profile_id : null
        );
        if (!form.selected_advance_id) {
          toast.error("Please select an advance payment.");
          return;
        }
        if (Number(form.temp_payment_amount) > remaining) {
          toast.error("Not enough balance in selected advance payment!");
          return;
        }
      }

      const currentDue = this.getCurrentDueAmount(invoice);
      if (Number(form.temp_payment_amount) > currentDue) {
        toast.error("Payment amount exceeds current due amount!");
        return;
      }

      // Validate new payment method specific fields
      if (form.temp_payment_method === 'Credit/Debit Card') {
        if (!form.card_type) { toast.error('Please select a card type.'); return; }
        if (!form.card_last_four) { toast.error('Please enter the last 4 digits of the card.'); return; }
        if (!/^\d{4}$/.test(form.card_last_four)) { toast.error('Last 4 digits must be exactly 4 numeric digits.'); return; }
      }
      if (form.temp_payment_method === 'Mobile Payment') {
        if (!form.mobile_payment_type) { toast.error('Please select a mobile payment type.'); return; }
        if (!form.mobile_provider) { toast.error('Please enter a provider.'); return; }
        if (!form.trans_ref_code) { toast.error('Please enter a reference number.'); return; }
      }
      if (form.temp_payment_method === 'Online Payment') {
        if (!form.online_gateway) { toast.error('Please enter a payment gateway.'); return; }
        if (!form.trans_ref_code) { toast.error('Please enter a reference number.'); return; }
      }

      const paymentIndex = this.invoicePayments.length;

      this.invoicePayments.push({
        invoice_no: invoice.invoices_no,
        date: new Date().toISOString(),
        payment_method: form.temp_payment_method,
        advance_ref_id: form.selected_advance_id || null,
        advance_ref: form.selected_advance_id ? "Advance Ref" : null,
        cheque_issue_bank_id: form.cheque_issue_bank_id || null,
        cheque_issue_branch_id: form.cheque_issue_branch_id || null,
        cheque_no: form.cheque_no || null,
        cheque_date: form.cheque_date || null,
        bank_account: form.bank_account || null,
        trans_ref_code: form.trans_ref_code || null,
        card_type: form.card_type || null,
        card_last4_digits: form.card_last_four || null,
        mobile_payment_type: form.mobile_payment_type || null,
        mobile_provider: form.mobile_provider || null,
        online_gateway: form.online_gateway || null,
        amount: Number(form.temp_payment_amount),
        index: paymentIndex
      });

      // Reset ALL form fields after successful payment add
      this.resetEntirePaymentForm(invoice);

      toast.success("Payment added successfully!");
    },

    // Resets ONLY specific fields on method change, retains method and amount
    onPaymentMethodChange(invoice) {
      const form = this.getPaymentForm(invoice.invoices_no);
      form.selected_advance_id = '';
      form.cheque_issue_bank_id = '';
      form.cheque_issue_branch_id = '';
      form.cheque_no = '';
      form.cheque_date = '';
      form.bank_account = '';
      form.trans_ref_code = '';
      form.card_type = '';
      form.card_last_four = '';
      form.mobile_payment_type = '';
      form.mobile_provider = '';
      form.online_gateway = '';
      form.branchesList = [];
    },

    // Resets ALL payment form fields for an invoice
    resetEntirePaymentForm(invoice) {
      const form = this.getPaymentForm(invoice.invoices_no);
      form.temp_payment_method = '';
      form.temp_payment_amount = '';
      form.selected_advance_id = '';
      form.cheque_issue_bank_id = '';
      form.cheque_issue_branch_id = '';
      form.cheque_no = '';
      form.cheque_date = '';
      form.bank_account = '';
      form.trans_ref_code = '';
      form.card_type = '';
      form.card_last_four = '';
      form.mobile_payment_type = '';
      form.mobile_provider = '';
      form.online_gateway = '';
      form.branchesList = [];
    },

    showDeleteConfirmation(title, message, action, params = null) {
      this.confirmationTitle = title;
      this.confirmationMessage = message;
      this.pendingAction = action;
      this.pendingActionParams = params;
      this.showConfirmationModal = true;
    },

    confirmAction() {
      if (this.pendingAction) {
        if (this.pendingActionParams) {
          this.pendingAction(...this.pendingActionParams);
        } else {
          this.pendingAction();
        }
      }
      this.closeConfirmation();
    },

    cancelConfirmation() {
      this.closeConfirmation();
    },

    closeConfirmation() {
      this.showConfirmationModal = false;
      this.pendingAction = null;
      this.pendingActionParams = null;
      this.confirmationTitle = '';
      this.confirmationMessage = '';
    },

    showDeleteSingleConfirmation(index) {
      this.showDeleteConfirmation(
          'Confirm Delete',
          'Do you want to delete this payment?',
          this.removeInvoicePayment,
          [index]
      );
    },

    showDeleteAllConfirmation(invoiceNo) {
      const paymentCount = this.getInvoicePayments(invoiceNo).length;
      if (paymentCount === 0) {
        useToast().warning("No payments to remove for this invoice.");
        return;
      }
      this.showDeleteConfirmation(
          'Confirm Delete',
          `Do you want to delete all ${paymentCount} payment(s) for invoice ${invoiceNo}?`,
          this.removeAllInvoicePayments,
          [invoiceNo]
      );
    },

    removeInvoicePayment(index) {
      this.invoicePayments.splice(index, 1);
      this.invoicePayments.forEach((payment, i) => { payment.index = i; });
      useToast().success("Payment removed successfully!");
    },

    removeAllInvoicePayments(invoiceNo) {
      this.invoicePayments = this.invoicePayments.filter(p => p.invoice_no !== invoiceNo);
      this.invoicePayments.forEach((payment, i) => { payment.index = i; });
      useToast().success(`Removed all payments for invoice ${invoiceNo}`);
    },

    getInvoicePayments(invoiceNo) {
      return this.invoicePayments
          .filter(p => p.invoice_no === invoiceNo)
          .sort((a, b) => a.index - b.index);
    },

    getInvoiceTotalPaid(invoiceNo) {
      const originalPaid = this.selectedInvoices.find(inv => inv.invoices_no === invoiceNo)?.total_paid || 0;
      const addedPayments = this.getInvoicePayments(invoiceNo)
          .reduce((total, payment) => total + Number(payment.amount), 0);
      return Number(originalPaid) + addedPayments;
    },

    getPaymentMethodIcon(method) {
      switch (method) {
        case 'Cash': return 'fa-solid fa-sack-dollar';
        case 'Cheque': return 'fa-solid fa-money-check';
        case 'Bank Transfer': return 'fa-solid fa-money-bill-transfer';
        case 'Advance': return 'fa-solid fa-money-bills';
        case 'Credit/Debit Card': return 'fa-solid fa-credit-card';
        case 'Mobile Payment': return 'fa-solid fa-mobile-screen-button';
        case 'Online Payment': return 'fa-solid fa-globe';
        default: return 'fa-solid fa-receipt';
      }
    },

    getPaymentReference(payment) {
      switch (payment.payment_method) {
        case 'Cheque':
          return payment.cheque_no ? `Cheque No: ${payment.cheque_no}` : 'Cheque';
        case 'Bank Transfer':
          return payment.trans_ref_code ? `Ref: ${payment.trans_ref_code}` : 'Bank Transfer';
        case 'Advance':
          return payment.advance_ref || 'Advance';
        case 'Credit/Debit Card': {
          const cardType = payment.card_type || 'Card';
          const last4 = payment.card_last4_digits || '';
          return last4 ? `${cardType} - ************${last4}` : cardType;
        }
        case 'Mobile Payment': {
          const rawType = payment.mobile_payment_type || '';
          const type = rawType.toUpperCase() === 'QR' ? 'QR' : rawType ? rawType.charAt(0).toUpperCase() + rawType.slice(1) : '';
          const provider = payment.mobile_provider || '';
          const ref = payment.trans_ref_code || '';
          const parts = [];
          if (type) parts.push(type);
          if (provider) parts.push(provider);
          if (ref) parts.push(`Ref: ${ref}`);
          return parts.length > 0 ? parts.join(' - ') : null;
        }
        case 'Online Payment': {
          const gateway = payment.online_gateway || '';
          const ref = payment.trans_ref_code || '';
          const parts = [];
          if (gateway) parts.push(gateway);
          if (ref) parts.push(`Ref: ${ref}`);
          return parts.length > 0 ? parts.join(' - ') : null;
        }
        default: return null;
      }
    },

    // Reset payment-specific fields on invoice object when method changes
    resetInvoicePaymentFields(invoice) {
      invoice.selected_advance_id = '';
      invoice.cheque_issue_bank_id = '';
      invoice.cheque_issue_branch_id = '';
      invoice.cheque_no = '';
      invoice.cheque_date = '';
      invoice.bank_account = '';
      invoice.trans_ref_code = '';
      invoice.card_type = '';
      invoice.card_last_four = '';
      invoice.mobile_payment_type = '';
      invoice.mobile_provider = '';
      invoice.online_gateway = '';
      invoice.branchesList = [];
    },


    isInvoiceExpanded(invoiceNo) {
      return !!this.expandedInvoices[invoiceNo];
    },

    toggleInvoiceExpansion(invoiceNo) {
      this.expandedInvoices = {
        ...this.expandedInvoices,
        [invoiceNo]: !this.expandedInvoices[invoiceNo]
      };
    },

    setPaymentListRef(invoiceNo, el) {
      if (el) this.paymentListRefs[invoiceNo] = el;
    },

    scrollPayments(invoiceNo, direction) {
      const listElement = this.paymentListRefs[invoiceNo];
      if (!listElement) return;
      listElement.scrollTo({ left: listElement.scrollLeft + direction * 280, behavior: 'smooth' });
      setTimeout(() => this.updateScrollButtons(invoiceNo), 300);
    },

    updateScrollButtons(invoiceNo) {
      const el = this.paymentListRefs[invoiceNo];
      if (!el) return;
      this.scrollStates[invoiceNo] = {
        isAtStart: el.scrollLeft <= 0,
        isAtEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
      };
    },

    isAtStart(invoiceNo) { return this.scrollStates[invoiceNo]?.isAtStart ?? true; },
    isAtEnd(invoiceNo) { return this.scrollStates[invoiceNo]?.isAtEnd ?? true; },

    // FIX: getTotalAdvanceForDealer now uses getAdvancesForDealer helper
    // so it correctly reads from advancePaymentsByDealer[dealerId].
    getTotalAdvanceForDealer(dealerId) {
      return this.getAdvancesForDealer(dealerId)
          .reduce((sum, adv) => sum + parseFloat(adv.balance || 0), 0);
    },

    async saveInvoicePayment() {
      const toast = useToast();
      if (this.saveButtonDisabled) return;
      this.saveButtonDisabled = true;

      if (this.invoicePayments.length === 0) {
        toast.error("Please add at least one invoice payment.");
        this.saveButtonDisabled = false;
        return;
      }

      const invoicesArray = this.invoicePayments.map(item => ({
        invoices_no: item.invoice_no,
        amount: item.amount,
        payment_method: item.payment_method,
        advance_payments_id: item.advance_ref_id || null,
        cheque_issue_bank_id: item.cheque_issue_bank_id || null,
        cheque_issue_branch_id: item.cheque_issue_branch_id || null,
        cheque_no: item.cheque_no || null,
        cheque_date: item.cheque_date || null,
        bank_account: item.bank_account || null,
        trans_ref_code: item.trans_ref_code || null,
        card_type: item.card_type || null,
        card_last4_digits: item.card_last4_digits || null,
        mobile_payment_type: item.mobile_payment_type || null,
        mobile_provider: item.mobile_provider || null,
        online_gateway: item.online_gateway || null,
      }));

      let payload;

      if (this.isGroupMode) {
        payload = {
          profile_id: this.profileId,
          dealer_group_id: this.dealerGroup?.group_id,
          amount: invoicesArray.reduce((sum, i) => sum + i.amount, 0),
          excessAmount: 0,
          invoices: invoicesArray,
        };
      } else {
        payload = {
          profile_id: this.profileId,
          dealer_id: this.dealer?.profile_id,
          amount: invoicesArray.reduce((sum, i) => sum + i.amount, 0),
          excessAmount: 0,
          invoices: invoicesArray,
        };
      }

      try {
        const response = await saveCompletedPay(payload, this.token);
        toast.success(response.data.message || "Invoice payments saved successfully.");
        this.invoicePayments = [];
        this.expandedInvoices = {};
        this.scrollStates = {};
        this.paymentListRefs = {};
        this.closeSettleInvoicePaymentModal();
      } catch (error) {
        console.error("Error:", error.response?.data || error);
        toast.error(error.response?.data?.message || "Failed to save invoice payments.");
      } finally {
        this.saveButtonDisabled = false;
      }
    },

    closeSettleInvoicePaymentModal() {
      this.invoicePayments = [];
      this.expandedInvoices = {};
      this.scrollStates = {};
      this.paymentListRefs = {};
      this.$emit('close');
    },
  }
};
</script>

<style scoped>
/* Dealer Section */
.dealer-section {
  padding: 0 25px;
}
.dealer-section-header {
  background: #f0f4ff;
  border-radius: 6px;
  border: 1px solid #d0d9f0;
  padding: 10px 16px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

/* Modal Structure */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.modal-content {
  background: #fff;
  box-shadow: -5px 0 20px rgba(0, 0, 0, 0.3);
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

/* Fixed Header */
.modal-header-fixed {
  background: white;
  border-bottom: 1px solid #e0e0e0;
  padding: 0;
  flex-shrink: 0;
  z-index: 10;
}
.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid #f0f0f0;
}
.popup-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.5rem;
  font-weight: 600;
}
.close-btn {
  border-radius: 50%;
  width: 36px;
  height: 36px;
  padding: 0;
  cursor: pointer;
  font-size: 16px;
  border: 1px solid #dee2e6;
  transition: all 0.2s;
}
.close-btn:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
}

/* Header Content */
.header-content {
  display: flex;
  gap: 20px;
  padding: 20px 25px;
  align-items: stretch;
}

/* Dealer Information Box */
.dealer-info-box {
  flex: 1;
  background: grey;
  border-radius: 10px;
  padding: 20px;
  color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}
.dealer-info-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
}
.dealer-info-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.dealer-info-item i {
  font-size: 16px;
  margin-top: 2px;
  opacity: 0.9;
  min-width: 16px;
}
.dealer-info-text { flex: 1; }
.dealer-info-label {
  font-size: 0.85rem;
  opacity: 0.8;
  margin-bottom: 4px;
  font-weight: 500;
}
.dealer-info-value {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
}

/* Amount Cards */
.amount-cards-container {
  display: flex;
  gap: 15px;
  flex: 0 0 auto;
}
.amount-card {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  border-radius: 10px;
  min-width: 200px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}
.amount-card-content {
  flex: 1;
  text-align: center;
}
.amount-card-label {
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 5px;
  opacity: 0.8;
}
.amount-card-value {
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.2;
}
.due-card {
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
  color: white;
}
.total-card {
  background: linear-gradient(135deg, #51cf66 0%, #40c057 100%);
  color: white;
}

/* Scrollable Body */
.modal-body-scrollable {
  flex: 1;
  overflow-y: auto;
  padding: 0;
  background: #f8f9fa;
}

/* Invoice Cards */
.invoice-cards-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 0 0 25px 0;
  padding: 0 25px;
}
.invoice-card {
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
  overflow: hidden;
  width: 100%;
}
.invoice-card.has-payments {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.invoice-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Card Header */
.invoice-card-header {
  background: #f8f9fa;
  padding: 15px 20px;
  border-bottom: 1px solid #e0e0e0;
  border-radius: 8px 8px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  cursor: pointer;
  transition: background-color 0.2s;
}
.invoice-card-header:hover { background-color: #f1f3f4; }
.invoice-header-right {
  display: flex;
  align-items: flex-start;
  gap: 15px;
}
.invoice-number {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 5px 0;
  color: #333;
}
.invoice-date {
  font-size: 13px;
  color: #666;
  margin: 0;
}
.invoice-amounts {
  display: flex;
  gap: 20px;
  margin-top: 10px;
}
.amount-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: flex-end;
}
.amount-label { font-size: 12px; color: #666; font-weight: 500; }
.amount-value { font-size: 14px; font-weight: 600; color: #333; }
.amount-value.paid { color: #28a745; }
.amount-value.due { color: #dc3545; }
.expand-invoice-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid #dee2e6;
  transition: all 0.2s;
}
.expand-invoice-btn:hover {
  background-color: #e9ecef;
  border-color: #adb5bd;
}

/* Collapsible */
.invoice-card-collapsible { border-top: 1px solid #e0e0e0; }

/* Card Body */
.invoice-card-body { padding: 20px; }
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 15px;
}
.form-group { display: flex; flex-direction: column; }
.form-group.full-width { grid-column: 1 / -1; }
.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  font-size: 13px;
  color: #333;
}

/* Amount Input */
.amount-input-container {
  position: relative;
  display: flex;
  align-items: stretch;
}
.amount-input-with-icon {
  width: 100%;
  padding: 10px 45px 10px 12px;
  border-radius: 5px 0 0 5px;
  border: 1px solid #ccc;
  border-right: none;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}
.amount-input-with-icon:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
  z-index: 1;
}
.fill-amount-btn {
  width: 40px;
  border: 1px solid #ccc;
  border-left: none;
  border-radius: 0 5px 5px 0;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #6c757d;
}
.fill-amount-btn:hover {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
}
.amount-hint {
  font-size: 11px;
  color: #6c757d;
  margin-top: 4px;
  font-style: italic;
}
.payment-details {
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
}

/* Card Footer */
.card-footer {
  margin-top: -20px;
  padding-top: 15px;
  display: flex;
  justify-content: flex-end;
}
.btn-add {
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 400;
  border-radius: 50px;
  background-color: #19183B;
  color: white;
}
.btn-add i { margin-right: 6px; }
.btn-add:hover { background-color: #343078; color: white; }

/* Two-Column Invoice Card Layout */
.invoice-card-two-col {
  display: flex;
  align-items: stretch;
  min-height: 250px;
}
.invoice-card-form-col {
  flex: 0 0 48%;
  max-width: 48%;
  overflow: hidden;
}
.invoice-col-divider {
  flex: 0 0 1px;
  background-color: #dee2e6;
  margin: 15px 0;
  align-self: stretch;
}
.invoice-card-payments-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px 20px;
  min-width: 0;
}
.payments-col-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e9ecef;
}
.payments-col-header h6 {
  font-size: 14px;
  font-weight: 600;
  color: #495057;
}
.payments-vertical-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  max-height: 380px;
  padding-right: 4px;
}
.payments-empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  text-align: center;
  padding: 20px;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  min-height: 150px;
}

/* Payment Cards (Vertical) */
.payment-card-vertical {
  background: white;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid #e9ecef;
}
.payment-card-vertical:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
}
.payment-card-content {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 100%;
}
.payment-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.payment-icon i { font-size: 14px; color: #495057; }
.payment-detail-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.payment-method-with-ref { font-weight: 600; font-size: 13px; color: #333; line-height: 1.3; }
.payment-reference-subtext { font-size: 12px; color: #6c757d; font-weight: 500; }
.payment-reference { font-weight: normal; color: #6c757d; font-size: 12px; }
.payment-amount-date { display: flex; flex-direction: column; gap: 2px; }
.payment-amount { font-weight: 600; font-size: 13px; color: #28a745; }
.payment-date { font-size: 11px; color: #6c757d; }
.payment-actions { display: flex; align-items: center; flex-shrink: 0; }
.btn-remove {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 12px;
  border: none;
}
.delete-all-btn {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
}

/* form-group spacing for new vertical layout */
.invoice-card-body .form-group.mb-3 {
  margin-bottom: 12px;
}

/* Save Button */
.save-button-container { padding: 20px 25px; }
.save-btn {
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  min-width: 160px;
}

/* Spinner */
.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}
.spinner {
  width: 60px;
  height: 60px;
  border: 5px solid rgba(0, 0, 0, 0.1);
  border-top-color: #007bff;
  border-radius: 50%;
  animation: spin 1s ease infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Form Elements */
.req { color: red; margin-left: 3px; }
label { display: block; margin-bottom: 5px; font-weight: bold; font-size: 14px; }
select {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}
input[type="date"],
input[type="number"],
input[type="text"] {
  width: 100%;
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}
select:focus, input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

/* Confirmation Modal */
.confirmation-overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 2000;
}
.confirmation-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
  overflow: hidden;
  animation: scaleIn 0.2s ease;
}
@keyframes scaleIn {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
.confirmation-header { padding: 20px 24px 0; }
.confirmation-header h4 { margin: 0; color: #dc3545; font-size: 1.25rem; font-weight: 600; }
.confirmation-body { padding: 20px 24px; }
.confirmation-body p { margin: 0; color: #495057; font-size: 14px; line-height: 1.5; }
.confirmation-footer {
  padding: 16px 24px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
.confirmation-footer .btn { padding: 8px 20px; border-radius: 6px; font-weight: 500; min-width: 80px; }

/* Responsive */
@media (max-width: 1024px) {
  .header-content { flex-direction: column; }
  .amount-cards-container { justify-content: space-between; }
  .amount-card { flex: 1; min-width: auto; }
  .invoice-card-form-col { flex: 0 0 50%; max-width: 50%; }
}
@media (max-width: 768px) {
  .modal-content { width: 98vw; height: 98vh; }
  .popup-header { padding: 15px 20px; }
  .header-content { padding: 15px 20px; }
  .amount-cards-container { flex-direction: column; }
  .amount-card { min-width: auto; }
  .invoice-card-header { flex-direction: column; gap: 15px; }
  .invoice-header-right { width: 100%; justify-content: space-between; }
  .invoice-amounts { align-self: flex-start; }
  .amount-item { align-items: flex-start; }
  .form-row { grid-template-columns: 1fr; }
  /* Stack columns on mobile */
  .invoice-card-two-col { flex-direction: column; }
  .invoice-card-form-col { flex: none; max-width: 100%; }
  .invoice-col-divider { flex: 0 0 1px; width: 100%; height: 1px; margin: 0 15px; }
  .invoice-card-payments-col { padding: 12px 15px; }
  .dealer-section { padding: 0; }
}
</style>